import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#initSwipeHint();
    this.#observeHiddenSlides();
  }

  #controller = new AbortController();
  #swipeHintTimeout;
  /** @type {MutationObserver | undefined} */
  #slideObserver;

  /**
   * The slideshow marks off-screen slides aria-hidden but their children stay
   * focusable (a video's play button), which is an accessibility violation
   * (axe: aria-hidden-focus). Mirror aria-hidden into `inert` so hidden slides
   * also leave the tab order; the attribute comes back off when the slide shows.
   */
  #observeHiddenSlides() {
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    if (slides.length === 0) return;
    /** @param {Element} slide */
    const sync = (slide) => slide.toggleAttribute('inert', slide.getAttribute('aria-hidden') === 'true');
    slides.forEach(sync);
    this.#slideObserver = new MutationObserver((records) => {
      for (const record of records) {
        if (record.target instanceof Element) sync(record.target);
      }
    });
    for (const slide of slides) {
      this.#slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#slideObserver?.disconnect();
    this.#controller.abort();
    if (this.#swipeHintTimeout) {
      window.clearTimeout(this.#swipeHintTimeout);
      this.#swipeHintTimeout = undefined;
    }
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #initSwipeHint() {
    const hint = this.querySelector('[data-swipe-hint]');
    if (!hint) return;
    if (this.media?.length <= 1) return;

    const prefersTouch = window.matchMedia('(hover: none) and (max-width: 749px)');
    if (!prefersTouch.matches) {
      this.dataset.swipeInteracted = 'true';
      return;
    }

    const { signal } = this.#controller;

    this.addEventListener('pointerdown', this.#markSwipeInteracted, { once: true, signal });
    this.addEventListener('touchstart', this.#markSwipeInteracted, { once: true, signal });

    const handleSelect = (event) => {
      if (event.detail?.userInitiated === false) return;
      this.#markSwipeInteracted();
    };
    this.addEventListener(SlideshowSelectEvent.eventName, handleSelect, { once: true, signal });

    this.#swipeHintTimeout = window.setTimeout(() => {
      this.#markSwipeInteracted();
    }, 4800);
  }

  #markSwipeInteracted = () => {
    if (this.dataset.swipeInteracted === 'true') return;
    this.dataset.swipeInteracted = 'true';
    if (this.#swipeHintTimeout) {
      window.clearTimeout(this.#swipeHintTimeout);
      this.#swipeHintTimeout = undefined;
    }
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
